import { normalizeHexColor } from '@eklevo/shared/brand'
import { characterCount } from '@eklevo/shared/text'
import { Pencil } from 'lucide-react'
import { useId, type ReactNode } from 'react'
import { Button } from '../../../components/ui/button'
import {
  legalValues,
  noticeLocales,
  WIZARD_STEPS,
  type WizardDraft,
} from '../../lib/company-wizard'
import { useI18n } from '../../lib/i18n'
import type { WizardCourse } from '../../lib/screens'

/** The legal data, in the order the first step asks for them. */
const LEGAL_SUMMARY = ['legalName', 'taxId', 'privacyContact', 'address'] as const

/*
 * The summary before «Crear empresa» (ADR 0009 §7): what each step will create, as typed, and the
 * legal data as the server will store them (a tax id without «ES», say), with the way back to each
 * one. The HR team's names and emails are shown here only, from the draft of this tab: the panel
 * never reads a person back (ADR 0008 §3).
 */
export function SummaryStep({
  draft,
  courses,
  onEdit,
}: {
  draft: WizardDraft
  /** The catalogue's courses the wizard offered, for their titles. */
  courses: readonly WizardCourse[]
  onEdit: (step: number) => void
}) {
  const { t } = useI18n()
  const title = new Map(courses.map((c) => [c.id, c.title]))
  const brand = draft.brand.enabled
    ? {
        primary: normalizeHexColor(draft.brand.primary) ?? draft.brand.primary,
        accent: normalizeHexColor(draft.brand.accent) ?? draft.brand.accent,
      }
    : null
  const legal = legalValues(draft)
  return (
    <div className="space-y-6">
      <p className="text-sm text-ink-muted">{t('wizard.summary.intro')}</p>

      <Section step="company" edit={t('wizard.summary.edit.company')} onEdit={onEdit}>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <Item term={t('wizard.company.name')}>{draft.name.trim()}</Item>
          <Item term={t('wizard.company.locale')}>{t(`wizard.locale.${draft.defaultLocale}`)}</Item>
          <Item term={t('wizard.company.timeZone')}>{t(`wizard.timeZone.${draft.timeZone}`)}</Item>
          {LEGAL_SUMMARY.map((field) => (
            <Item key={field} term={t(`wizard.company.${field}`)}>
              <span className="[overflow-wrap:anywhere]">
                {legal[field] ?? t('wizard.summary.none')}
              </span>
            </Item>
          ))}
          <Item term={t('wizard.brand')}>
            {brand ? (
              <span className="flex flex-wrap gap-x-4 gap-y-1">
                <Swatch color={brand.primary} label={t('wizard.brand.primary')} />
                <Swatch color={brand.accent} label={t('wizard.brand.accent')} />
              </span>
            ) : (
              t('wizard.summary.brand.none')
            )}
          </Item>
          <Item term={t('wizard.logo')}>
            {draft.logo?.status === 'ready' ? (
              <span className="[overflow-wrap:anywhere]">{draft.logo.file}</span>
            ) : (
              t('wizard.summary.logo.none')
            )}
          </Item>
        </dl>
      </Section>

      <Section step="people">
        <p className="text-sm">{t('wizard.summary.people')}</p>
      </Section>

      <Section step="hr" edit={t('wizard.summary.edit.hr')} onEdit={onEdit}>
        <ul className="space-y-1 text-sm">
          {draft.hrTeam.map((p) => (
            <li key={p.key} className="[overflow-wrap:anywhere]">
              <span className="font-medium">{`${p.firstName.trim()} ${p.lastName.trim()}`}</span>
              <span className="text-ink-muted">{` · ${p.email.trim().toLowerCase()}`}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-ink-muted">
          {t('wizard.summary.invitations', { count: draft.hrTeam.length })}
        </p>
      </Section>

      <Section step="notice" edit={t('wizard.summary.edit.notice')} onEdit={onEdit}>
        <dl className="grid gap-y-3 text-sm">
          {noticeLocales(draft).map((locale) => {
            const n = draft.notice[locale]
            return (
              <Item key={locale} term={t(`wizard.notice.legend.${locale}`)}>
                {n.kind === 'url' ? (
                  <>
                    {`${t('wizard.summary.notice.url')}: `}
                    <span className="[overflow-wrap:anywhere]">{n.url.trim()}</span>
                  </>
                ) : (
                  t('wizard.summary.notice.text', { count: characterCount(n.body.trim()) })
                )}
              </Item>
            )
          })}
        </dl>
      </Section>

      <Section step="courses" edit={t('wizard.summary.edit.courses')} onEdit={onEdit}>
        {draft.courses.length === 0 ? (
          <p className="text-sm">{t('wizard.summary.courses.none')}</p>
        ) : (
          <>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {draft.courses.map((id) => (
                <li key={id}>{title.get(id) ?? t('catalog.untitled')}</li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-ink-muted">{t('company.activeCoursesHint')}</p>
          </>
        )}
      </Section>
    </div>
  )
}

function Section({
  step,
  edit,
  onEdit,
  children,
}: {
  step: (typeof WIZARD_STEPS)[number]
  /** The name of the way back to the step, if it has one. */
  edit?: string
  onEdit?: (step: number) => void
  children: ReactNode
}) {
  const { t } = useI18n()
  const id = useId()
  return (
    <section aria-labelledby={id} className="rounded-lg border border-line p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={id} className="font-medium">
          {t(`wizard.step.${step}`)}
        </h3>
        {edit && onEdit && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={edit}
            onClick={() => onEdit(WIZARD_STEPS.indexOf(step))}
          >
            <Pencil aria-hidden className="size-4" />
            {t('wizard.summary.edit')}
          </Button>
        )}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  )
}

function Item({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-ink-muted">{term}</dt>
      <dd className="mt-0.5 [overflow-wrap:anywhere]">{children}</dd>
    </div>
  )
}

/** A colour as a swatch with its name and value (the swatch itself says nothing more). */
export function Swatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden
        className="inline-block size-4 rounded border border-line"
        style={{ backgroundColor: color }}
      />
      <span>
        {label} <span className="font-mono">{color}</span>
      </span>
    </span>
  )
}
